import React from 'react';
import { Col, Form, InputGroup, Row } from '@themesberg/react-bootstrap';
import DatePickerField from './DatePickerField';
import PoPaymentTermSelect from './PoPaymentTermSelect';
import QuoteProductsTable from './QuoteProductsTable';
import PoInvoicesTable from './PoInvoicesTable';
import { poSalesStatusOptions, poToPrincipalVendorOptions } from '../utils/poSales';
import { quoteProductSummary, poProductsSchema } from '../utils/quoteProducts';
import * as Yup from 'yup';

export const poSalesFormSchema = (terms, savedPaymentTerm = '', requirePoNumber = false) => Yup.object({
  products: poProductsSchema,
  // Typed once when converting; existing POs show it read-only and are never re-validated.
  po_number: requirePoNumber ? Yup.string().trim().max(100).required('PO Number is required') : Yup.string(),
  po_release_date: Yup.string().required('PO release date is required'),
  po_received_date: Yup.string().required('PO received date is required'),
  delivery_due_date: Yup.string().required('Delivery due date is required'),
  po_to_principal_vendor: Yup.string().oneOf(poToPrincipalVendorOptions).required('Please select Yes, No or Stock'),
  principal_reference_number: Yup.string().trim().max(100, 'Reference number cannot exceed 100 characters'),
  principal_reference_date: Yup.string(),
  pi: Yup.string().oneOf(['Yes', 'No']),
  pi_reference_number: Yup.string().trim().max(100, 'Reference number cannot exceed 100 characters'),
  pi_reference_date: Yup.string(),
  payment_term: Yup.string().max(100).required('Payment term is required')
    .oneOf([...terms.map(term => term.name), savedPaymentTerm].filter(Boolean), 'Select an available payment term.'),
  status: Yup.string().oneOf(poSalesStatusOptions).required('Status is required'),
  invoices: Yup.array().max(100, 'A PO can contain up to 100 invoices').of(Yup.object({
    // A completely blank row is ignored; once any field is filled, all four are required.
    invoice_no: Yup.string().trim().max(100, 'Invoice number cannot exceed 100 characters'),
    invoice_date: Yup.string(),
    transporter_name: Yup.string().trim().max(150, 'Transporter name cannot exceed 150 characters'),
    reference_number: Yup.string().trim().max(100, 'Reference number cannot exceed 100 characters'),
  }).test('invoice-complete', 'Invoice is incomplete', function (row) {
    const required = [
      ['invoice_no', 'Invoice number is required'], ['invoice_date', 'Invoice date is required'],
      ['transporter_name', 'Transporter name is required'], ['reference_number', 'Reference number is required'],
    ];
    const filled = field => String(row?.[field] || '').trim() !== '';
    if (!required.some(([field]) => filled(field))) return true;
    const errors = required.filter(([field]) => !filled(field))
      .map(([field, message]) => this.createError({ path: this.path + '.' + field, message }));
    return errors.length ? new Yup.ValidationError(errors) : true;
  })),
  remarks: Yup.string(),
});

export const poSalesPayload = values => ({
  products: values.products.map(({ id, _key, principal_id, category_id, ...row }) => ({ ...(id != null ? { id } : {}), ...row })),
  po_release_date: values.po_release_date,
  po_received_date: values.po_received_date,
  delivery_due_date: values.delivery_due_date,
  po_to_principal_vendor: values.po_to_principal_vendor,
  ...(values.pi !== undefined ? {
    principal_reference_number: values.po_to_principal_vendor === 'Yes' ? (values.principal_reference_number || '').trim() || null : null,
    principal_reference_date: values.po_to_principal_vendor === 'Yes' ? values.principal_reference_date || null : null,
    pi: values.pi,
    pi_reference_number: values.pi === 'Yes' ? (values.pi_reference_number || '').trim() || null : null,
    pi_reference_date: values.pi === 'Yes' ? values.pi_reference_date || null : null,
  } : {}),
  payment_term: values.payment_term,
  status: values.status,
  ...(values.invoices !== undefined ? {
    // Blank rows are dropped; a saved invoice that was cleared is removed.
    invoices: values.invoices
      .filter(row => ['invoice_no', 'invoice_date', 'transporter_name', 'reference_number'].some(field => String(row[field] || '').trim()))
      .map(({ id, invoice_no, invoice_date, transporter_name, reference_number }) => ({
        ...(id != null ? { id } : {}), invoice_no: (invoice_no || '').trim(), invoice_date,
        transporter_name: (transporter_name || '').trim(), reference_number: (reference_number || '').trim(),
      })),
  } : {}),
  remarks: values.remarks,
});

const formatDetailDate = value => {
  const stored = String(value || '').slice(0, 10);
  const [year, month, day] = stored.split('-');
  return year && month && day ? `${month}/${day}/${year}` : (value || '');
};

const QuoteValue = ({ label, value }) => (
  <Col md={3} className="mb-3">
    <div className="po-sales-quote-detail">
      <div className="po-sales-quote-detail__label">{label}</div>
      <div className="po-sales-quote-detail__value">{value || value === 0 ? value : '—'}</div>
    </div>
  </Col>
);

export default function PoSalesFormFields({ formik, quote, terms, actions, poNumberReadOnly = false, classificationInProducts = false, invoiceSection = false, editDetails = false }) {
  const fields = [
    ['po_number', 'PO Number', 'po-number'],
    ['po_release_date', 'PO Release Date', 'date'],
    ['po_received_date', 'PO Received Date', 'date'],
    ['po_amount', 'PO Amount', 'money'],
    ['delivery_due_date', 'Delivery Due Date', 'date'],
    ['po_to_principal_vendor', 'PO to Principal/Vendor', 'select', poToPrincipalVendorOptions],
    ...(editDetails && formik.values.po_to_principal_vendor === 'Yes' ? [
      ['principal_reference_number', 'Reference Number', 'text'],
      ['principal_reference_date', 'Date', 'date'],
    ] : []),
    ['payment_term', 'Payment Term', 'payment-term'],
    ...(editDetails ? [['pi', 'PI', 'select', ['Yes', 'No']]] : []),
    ...(editDetails && formik.values.pi === 'Yes' ? [
      ['pi_reference_number', 'PI Reference Number', 'text'],
      ['pi_reference_date', 'PI Date', 'date'],
    ] : []),
    ['status', 'Status', 'select', poSalesStatusOptions],
  ];

  return (
    <>
      {formik.status && <div role="alert" className="alert alert-danger">{formik.status}</div>}
      <Row className="mb-3">
        <Col md={12}><h5 className="mb-3">Quote Details</h5></Col>
        <QuoteValue label="System Quote No." value={quote?.system_quote_no} />
        <QuoteValue label="Offer No." value={quote?.offer_no} />
        <QuoteValue label="Customer" value={quote?.customer?.name} />
        <QuoteValue label="Company" value={quote?.company?.name} />
        <QuoteValue label="Branch" value={quote?.branch?.name} />
        {!classificationInProducts && <>
          <QuoteValue label="Principal / Vendor" value={quote?.principal?.name} />
          <QuoteValue label="Category" value={quote?.category?.name} />
        </>}
        <QuoteValue label="Offer Date" value={formatDetailDate(quote?.offer_date)} />
        <QuoteValue label="Expected Date" value={formatDetailDate(quote?.expected_date)} />
        <Col md={12} className="mb-3">
          <Form.Group controlId="po-sales-remarks">
            <Form.Label>Remarks</Form.Label>
            <Form.Control as="textarea" rows={3} {...formik.getFieldProps('remarks')}
              disabled={formik.isSubmitting} isInvalid={Boolean(formik.touched.remarks && formik.errors.remarks)} />
            <Form.Control.Feedback type="invalid">{formik.errors.remarks}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <QuoteProductsTable compactRows={classificationInProducts} showClassification={classificationInProducts} formik={formik}
        classificationInfo={classificationInProducts ? {
          principal_id: quote?.principal?.name || '—',
          category_id: quote?.category?.name || '—',
        } : null} />
      {invoiceSection && <PoInvoicesTable formik={formik} />}
      <hr />

      <Row className="mb-3">
        <Col md={12}><h5 className="mb-3">PO Sales Details</h5></Col>
        {fields.map(([name, label, type, options]) => (
          <Col md={3} className="mb-3" key={name}>
            {type === 'payment-term' ? (
              <PoPaymentTermSelect id="po-sales-payment-term" formik={formik}
                terms={terms} savedValue={formik.values.payment_term || ''} />
            ) : (
              <Form.Group controlId={'po-sales-' + name}>
                <Form.Label htmlFor={'po-sales-' + name}>{label}{!['stock_reference_', 'principal_reference_', 'pi_reference_'].some(prefix => name.startsWith(prefix)) && name !== 'pi' && name !== 'status' && name !== 'po_amount' && !(name === 'po_number' && poNumberReadOnly) && <span className="text-danger">*</span>}</Form.Label>
                {type === 'select' ? (
                  <Form.Select id={'po-sales-' + name} {...formik.getFieldProps(name)} disabled={formik.isSubmitting}
                    isInvalid={Boolean(formik.touched[name] && formik.errors[name])}>
                    <option value="">Select {label}</option>
                    {options.map(option => <option key={option} value={option}>{option}</option>)}
                  </Form.Select>
                ) : type === 'po-number' ? (
                  <Form.Control id="po-sales-po_number" {...formik.getFieldProps('po_number')} maxLength={100}
                    readOnly={poNumberReadOnly} disabled={formik.isSubmitting && !poNumberReadOnly}
                    isInvalid={Boolean(formik.touched.po_number && formik.errors.po_number)} />
                ) : type === 'money' ? (
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    {/* Always the Quote total of the product table; never typed in by the user. */}
                    <Form.Control id={'po-sales-' + name} name={name} readOnly
                      value={quoteProductSummary(formik.values.products).total} />
                  </InputGroup>
                ) : type === 'freight' ? (
                  <InputGroup hasValidation>
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control id={'po-sales-' + name} {...formik.getFieldProps(name)} inputMode="decimal" maxLength={16}
                      disabled={formik.isSubmitting} isInvalid={Boolean(formik.touched[name] && formik.errors[name])} />
                    <Form.Control.Feedback type="invalid">{formik.errors[name]}</Form.Control.Feedback>
                  </InputGroup>
                ) : type === 'date' ? (
                  <DatePickerField
                    id={'po-sales-' + name}
                    name={name}
                    value={formik.values[name]}
                    onChange={value => formik.setFieldValue(name, value)}
                    onBlur={() => formik.setFieldTouched(name, true)}
                    disabled={formik.isSubmitting}
                    isInvalid={Boolean(formik.touched[name] && formik.errors[name])}
                  />
                ) : (
                  <Form.Control id={'po-sales-' + name} {...formik.getFieldProps(name)} maxLength={name === 'transporter_name' ? 150 : name.endsWith('_reference_number') ? 100 : undefined} disabled={formik.isSubmitting}
                    isInvalid={Boolean(formik.touched[name] && formik.errors[name])} />
                )}
                <Form.Control.Feedback type="invalid">{formik.errors[name]}</Form.Control.Feedback>
              </Form.Group>
            )}
          </Col>
        ))}
      </Row>
      {actions}
    </>
  );
}
